import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  ExpandMore,
  Inventory2Outlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useStatisticsQuery } from '../../hooks/useStatisticsQuery';
import {
  StatisticsResponse,
  StatisticsRow,
} from '../../types/supplierStatistics';
import StatisticsChannels from './StatisticsChannels';
import StatisticsProducts from './StatisticsProducts';
import StatisticsSummary from './StatisticsSummary';
import StatisticsTable from './StatisticsTable';
import StatisticsTrends from './StatisticsTrends';

export default function StatisticsDashboard({
  data,
  params,
  refresh,
  detailHref,
  brandHref,
  supplierHref,
  suppliersHref,
  brandsHref,
}: {
  data: StatisticsResponse;
  params: Record<string, string | number>;
  refresh: number;
  detailHref: (row: StatisticsRow) => string;
  brandHref: (row: StatisticsRow) => string;
  supplierHref: (row: StatisticsRow) => string;
  suppliersHref: string;
  brandsHref: string;
}) {
  const suppliers = useStatisticsQuery<StatisticsResponse>(
    '/suppliers',
    { ...params, page: 1, limit: 5, sortBy: 'revenue', sortOrder: 'DESC' },
    refresh,
  );
  const [showReorder, setShowReorder] = React.useState(false);
  return (
    <>
      <StatisticsSummary row={data.summary} />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            {data.summary && <StatisticsChannels row={data.summary} />}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              height: '100%',
              bgcolor: '#fffbf3',
              borderColor: 'warning.light',
            }}
          >
            <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
              <Inventory2Outlined color="warning" />
              <Typography fontWeight={700}>Håll koll på lagret</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {data.reorderAlerts?.length
                ? `${data.reorderAlerts.length} produkter / färger i påfyllnadsurvalet har högst 14 dagars beräknad täckning.`
                : 'Inga påfyllnadsvarningar i underlaget för vald period.'}
            </Typography>
            {!!data.reorderAlerts?.length && (
              <Button
                href="#statistics-reorder"
                onClick={() => setShowReorder(true)}
                endIcon={<ArrowForward />}
                sx={{ mt: 1, px: 0 }}
              >
                Visa påfyllnad
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700}>
          Produkter som driver försäljningen
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Högst omsättning under vald period · Klicka för färger, storlekar och
          historik
        </Typography>
        <StatisticsProducts
          rows={data.topSellingColors || []}
          href={detailHref}
          preview
        />
      </Box>
      <StatisticsTrends params={params} refresh={refresh} />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} xl={6} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="h6" fontWeight={700}>
              Leverantörer
            </Typography>
            <Button
              component={RouterLink}
              to={suppliersHref}
              endIcon={<ArrowForward />}
            >
              Alla leverantörer
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            De fem största efter omsättning
          </Typography>
          {suppliers.error ? (
            <Alert severity="error">
              Leverantörsöversikten kunde inte hämtas.{' '}
              <Button component={RouterLink} to={suppliersHref}>
                Öppna listan
              </Button>
            </Alert>
          ) : suppliers.loading ? (
            <Box role="status" sx={{ py: 3 }}>
              <CircularProgress size={24} aria-label="Hämtar leverantörer" />
            </Box>
          ) : suppliers.data?.suppliers?.length ? (
            <StatisticsTable
              rows={suppliers.data.suppliers}
              level="supplier"
              href={supplierHref}
              compact
            />
          ) : (
            <Typography color="text.secondary">
              Inga leverantörer i underlaget.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} xl={6} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="h6" fontWeight={700}>
              Varumärken
            </Typography>
            <Button
              component={RouterLink}
              to={brandsHref}
              endIcon={<ArrowForward />}
            >
              Alla varumärken
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            De fem största efter omsättning
          </Typography>
          {data.topBrandsByRevenue?.length ? (
            <StatisticsTable
              rows={data.topBrandsByRevenue.slice(0, 5)}
              level="brand"
              href={brandHref}
              compact
            />
          ) : (
            <Typography color="text.secondary">
              Inga varumärken i underlaget.
            </Typography>
          )}
        </Grid>
      </Grid>
      <Accordion
        id="statistics-reorder"
        expanded={showReorder}
        onChange={(_, expanded) => setShowReorder(expanded)}
        variant="outlined"
        disableGutters
        sx={{ mb: 2, scrollMarginTop: 80 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={700}>
            Påfyllnad · högst 14 dagars täckning
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StatisticsProducts
            rows={data.reorderAlerts || []}
            href={detailHref}
            preview
          />
        </AccordionDetails>
      </Accordion>
      <Accordion variant="outlined" disableGutters sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={700}>Överlager</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Över 180 dagars täckning eller minst 90 dagar utan positiv
            försäljningstakt. Lagerurvalen visar högst tio produkter / färger
            vardera.
          </Typography>
          <StatisticsProducts
            rows={data.overstocked || []}
            href={detailHref}
            preview
          />
        </AccordionDetails>
      </Accordion>
      {data.movers && (
        <Accordion variant="outlined" disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={700}>
              Leverantörer med störst förändring
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(
              [
                ['revenueUp', 'Störst ökning i omsättning'],
                ['revenueDown', 'Störst minskning i omsättning'],
                ['unitsUp', 'Störst ökning i antal¹'],
                ['unitsDown', 'Störst minskning i antal¹'],
              ] as const
            ).map(([key, label]) => (
              <Box key={key} sx={{ mb: 3 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  {label}
                </Typography>
                {data.movers?.[key].length ? (
                  <StatisticsTable
                    rows={data.movers[key]}
                    level="supplier"
                    href={supplierHref}
                  />
                ) : (
                  <Typography color="text.secondary">
                    Inga observerade förändringar.
                  </Typography>
                )}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
